// Workflow validation utilities

// Helper functions for attribute transformation (used in payload building)
export function transformAttributes(attrs) {  
  const attrList = Object.values(attrs).filter(attr => typeof attr === "object" && attr?.key);

  const command = attrList.find(attr => attr.key === "command")?.value || "";
  const mcp_args = attrList.find(attr => attr.key === "mcp_args")?.value || [];

  // Build env data
  const envdata = {};
  const mcpEnvList = attrList.find(attr => attr.key === "Mcp Environment")?.value || [];

  mcpEnvList.forEach(env => {
    const key = env.key;
    // Prefer top-level attrs[key] if present, else fallback to env.value
    if (attrs[key] !== undefined && attrs[key] !== "") {
      envdata[key] = attrs[key];
    } else if (env.value !== undefined && env.value !== "") {
      envdata[key] = env.value;
    }
  });

  return [{
    command,
    mcp_args,
    env: envdata
  }];
}

export function cleanAttributes(attrs) {

  if (Array.isArray(attrs)) {
    // Check if this is a default attributes array (like [{key: "private_token", type: "password", required: false}, ...])
    // If it's just default values with no edits, return {}
    const isDefaultArray = attrs.every(attr => 
      typeof attr === 'object' && 
      attr !== null && 
      'key' in attr && 
      'type' in attr && 
      'required' in attr &&
      !('value' in attr) // No value property means no edits
    );
    
    if (isDefaultArray) {
      return [];
    }
    
    // Return array format as-is if it has been edited
    return attrs;
  }
  
  // Handle object format (backward compatibility) - convert to array
  if (attrs && typeof attrs === 'object') {
    const attrArray = [];
    const processedKeys = new Set();
    let hasAnyValue = false;
    
    for (const [key, value] of Object.entries(attrs)) {
      // Skip numeric keys (metadata) and _isValue/_hasOauth keys
      if (/^\d+$/.test(key) || key.endsWith('_isValue') || key.endsWith('_hasOauth')) continue;
      
      if (processedKeys.has(key)) continue;
      processedKeys.add(key);
      
      // Handle object format: { key: { value: "...", isValue: true } } or { key: { value: "...", hasOauth: true, provider: "..." } }
      if (typeof value === 'object' && value !== null && 'value' in value) {
        // Check if value is not empty
        const attrValue = value.value;
        if (attrValue !== '' && attrValue !== null && attrValue !== undefined) {
          hasAnyValue = true;
        }

        // Special case: provider-backed select attributes like "name"/"method"
        // should send both id + label (backend expects `name` to be the label).
        // We store selects as `{ value: <id>, id, name }` in NodeConfigSidebar.
        const attrObj =
          key === 'name'
            ? {
                name: value?.name != null && value?.name !== '' ? value.name : attrValue,
                id: value?.id != null && value?.id !== '' ? value.id : attrValue,
              }
            : {
                [key]: attrValue,
              };

        // If select metadata exists, include it (useful for backend / traces)
        if (key !== 'name' && (value?.id != null || value?.name != null)) {
          if (value?.id != null) attrObj.id = value.id;
          if (value?.name != null) attrObj.name = value.name;
        }

        // Support both old isValue and new hasOauth/provider format
        if (value.hasOauth !== undefined) {
          attrObj.hasOauth = value.hasOauth;
          attrObj.provider = value.provider || null;
        } else if (value.isValue !== undefined) {
          // Convert old isValue to hasOauth
          attrObj.hasOauth = value.isValue;
          attrObj.provider = null;
        } else {
          // Default: no OAuth
          attrObj.hasOauth = false;
          attrObj.provider = null;
        }
        attrArray.push(attrObj);
      } 
      // Handle flat format: { key: "value", key_isValue: true } or { key: "value", key_hasOauth: true }
      else {
        // Check if value is not empty
        if (value !== '' && value !== null && value !== undefined) {
          hasAnyValue = true;
        }
        
        const hasOauthKey = `${key}_hasOauth`;
        const isValueKey = `${key}_isValue`;
        const hasOauth = attrs[hasOauthKey] || attrs[isValueKey] || false;
        const attrObj = {
          [key]: value
        };
        attrObj.hasOauth = hasOauth;
        // Try to get provider if available, otherwise set to null
        attrObj.provider = (hasOauth && attrs.provider) ? attrs.provider : null;
        attrArray.push(attrObj);
      }
    }
    
    // If no values were set (all empty), return {}
    if (!hasAnyValue && attrArray.length > 0) {
      return [];
    }
    
    return attrArray.length > 0 ? attrArray : [];
  }
  
  return [];
}

// Validation function to check workflow structure (topology/connections)
export const createValidateWorkflowStructure = (showAlert, setUnconnectedNodeIds, setInvalidAgentIds) => {
  return (currentNodes, currentEdges) => {
    // Check if there are any nodes in the workflow
    if (currentNodes.length === 0) {
      showAlert('Please add nodes to the workflow before running', 'warning');
      return false;
    }

    // Validation: must have one input, at least one agent, and edges connecting them
    const inputNodes = currentNodes.filter(n => n.type === 'input');
    const agentNodes = currentNodes.filter(n => n.type === 'agent');
    const taskNodes = currentNodes.filter(n => n.type === 'task');
    
    if (inputNodes.length !== 1) {
      showAlert('Workflow must have exactly one input node.');
      return false;
    }
    
    // Check for at least one edge from input to agent
    const inputId = inputNodes[0]?.id;
    const agentIds = agentNodes.map(a => a.id);
    const taskIds = taskNodes.map(t => t.id);
    const hasInputToAgent = currentEdges.some(e => e.source === inputId && agentIds.includes(e.target));
    const hasInputToTask = currentEdges.some(e => e.source === inputId && taskIds.includes(e.target));
    
    if (!hasInputToAgent && !hasInputToTask) {
      showAlert('Input node must connect to an agent or task.');
      return false;
    }
    
    // Highlight unconnected nodes (exclude output nodes)
    const connectedNodeIds = new Set();
    currentEdges.forEach(e => {
      connectedNodeIds.add(e.source);
      connectedNodeIds.add(e.target);
    });
    const unconnected = currentNodes.filter(n => 
      !connectedNodeIds.has(n.id) && n.type !== 'output'
    );
    
    if (unconnected.length > 0) {
      setUnconnectedNodeIds(unconnected.map(n => n.id));
      showAlert('Some nodes are not connected: ' + unconnected.map(n => n.data?.title || n.data?.label || n.type).join(', '));
      return false;
    } else {
      setUnconnectedNodeIds([]);
    }
    
    // Note: Dead-end check removed - agent/task nodes can be terminal nodes (endpoints)
    // Output nodes are no longer required, so agents can be the final nodes in the workflow
    
    // Validate each agent has at least one tool and one llm config edge
    const invalidAgents = agentNodes.filter(agent => {
      const outgoing = currentEdges.filter(e => e.source === agent.id);
      const toolEdge = outgoing.find(e => {
        const targetNode = currentNodes.find(n => n.id === e.target);
        return targetNode && targetNode.type === 'config' && targetNode.data.configType === 'tool';
      });
      const llmEdge = outgoing.find(e => {
        const targetNode = currentNodes.find(n => n.id === e.target);
        return targetNode && targetNode.type === 'config' && targetNode.data.configType === 'llm';
      });
      return !(toolEdge && llmEdge);
    });
    
    if (invalidAgents.length > 0) {
      setInvalidAgentIds(invalidAgents.map(a => a.id));
      showAlert('Agent must be connected to at least one Tool and one LLM.');
      return false;
    } else {
      setInvalidAgentIds([]);
    }
    
    // Validate each task agent has at least one tool connected
    const invalidTaskAgents = taskNodes.filter(task => {
      const outgoing = currentEdges.filter(e => e.source === task.id && e.sourceHandle === 'tool');
      const toolEdge = outgoing.find(e => {
        const targetNode = currentNodes.find(n => n.id === e.target);
        return targetNode && targetNode.type === 'config' && targetNode.data.configType === 'tool';
      });
      return !toolEdge;
    });
    
    if (invalidTaskAgents.length > 0) {
      const taskNames = invalidTaskAgents.map(t => t.data?.title || 'Task Agent').join(', ');
      showAlert(`Task agent${invalidTaskAgents.length > 1 ? 's' : ''} must be connected to at least one Tool`);
      return false;
    }
    
    return true;
  };
};

// Helper function to get attribute value from node attributes (handles both object and primitive formats)
const getAttributeValueFromNode = (attributes, key) => {
  if (!attributes) return '';
  const attrValue = attributes[key];
  if (attrValue === null || attrValue === undefined) return '';
  // If it's an object with value property, extract the value
  if (typeof attrValue === 'object' && attrValue !== null && 'value' in attrValue) {
    const value = attrValue.value;
    // Handle boolean false and 0 explicitly (they are falsy but valid values)
    if (value === false || value === 0) return value;
    return value || '';
  }
  // Otherwise return as-is (backward compatibility)
  return attrValue;
};

const getConnectedAppIdFromAttribute = (attributes, key) => {
  if (!attributes) return '';
  const attrValue = attributes[key];
  if (attrValue === null || attrValue === undefined) return '';
  if (typeof attrValue === 'object' && attrValue !== null) {
    const id = attrValue.id ?? attrValue.value;
    if (id === false || id === 0) return String(id);
    return id != null && id !== '' ? String(id) : '';
  }
  return String(attrValue);
};

const isProviderBackedSelectAttr = (attr, storedAttr) => {
  if (!attr || attr.type !== 'select') return false;
  if (attr.source === 'oauth') return true;
  if (storedAttr && typeof storedAttr === 'object' && storedAttr.hasOauth) return true;
  return false;
};

const formatAttrLabel = (key) =>
  String(key || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Validates that provider-backed tool select attributes still reference
 * an existing connected app (e.g. Gmail handler System Config).
 * Blocks save/run when the configured connected app was deleted.
 */
export const createValidateConnectedAppReferences = (
  setSelectedNode,
  setDrawerOpen,
  setNodes,
  toolNodesOrGetter = [],
  fetchAuthProvider
) => {
  const getToolNodes = () =>
    typeof toolNodesOrGetter === 'function' ? toolNodesOrGetter() || [] : toolNodesOrGetter || [];

  return async (currentNodes, onValidationError) => {
    if (typeof fetchAuthProvider !== 'function') return true;

    const toolNodes = getToolNodes();
    const toolConfigNodes = currentNodes.filter(
      (n) => n.type === 'config' && n.data?.configType === 'tool' && n.data?.handlerName !== 'MCPHandler'
    );

    if (toolConfigNodes.length === 0) return true;

    const providerCache = new Map(); // provider -> Set(ids)

    const loadProviderAppIds = async (provider) => {
      const key = String(provider || '').toUpperCase();
      if (!key) return null;
      if (providerCache.has(key)) return providerCache.get(key);

      try {
        const response = await fetchAuthProvider(key, 0, 100);
        const apps = response?.thirdPartyAppsDTOPage?.content || [];
        const ids = new Set(apps.map((app) => String(app.id)));
        providerCache.set(key, ids);
        return ids;
      } catch (error) {
        console.error(`Error validating connected apps for provider ${key}:`, error);
        providerCache.set(key, null);
        return null;
      }
    };

    for (const toolNode of toolConfigNodes) {
      const toolDefinition = toolNodes.find((t) => t.handlerName === toolNode.data?.handlerName);
      if (!toolDefinition?.attributes?.length) continue;

      const provider = toolNode.data?.provider;
      if (!provider) continue;

      const providerAppIds = await loadProviderAppIds(provider);
      // If provider lookup failed, skip rather than blocking save on network issues
      if (providerAppIds == null) continue;

      for (const attr of toolDefinition.attributes) {
        const storedAttr = toolNode.data?.attributes?.[attr.key];
        if (!isProviderBackedSelectAttr(attr, storedAttr)) continue;

        const selectedId = getConnectedAppIdFromAttribute(toolNode.data?.attributes, attr.key);
        if (!selectedId) {
          if (!attr.required) continue;
          const attrLabel = formatAttrLabel(attr.key);
          const toolName = toolNode.data?.displayName || toolDefinition.displayName || toolNode.data?.handlerName;
          const updatedNode = {
            ...toolNode,
            data: { ...toolNode.data, errorMessage: attr.key }
          };
          setSelectedNode(updatedNode);
          setDrawerOpen(true);
          setNodes((nds) => nds.map((n) => (n.id === toolNode.id ? updatedNode : n)));
          if (onValidationError) {
            onValidationError(
              `${toolName} is missing required configuration: ${attrLabel}. Please configure the tool node.`,
              'error'
            );
          }
          return false;
        }

        if (!providerAppIds.has(String(selectedId))) {
          const attrLabel = formatAttrLabel(attr.key);
          const toolName = toolNode.data?.displayName || toolDefinition.displayName || toolNode.data?.handlerName;
          const updatedNode = {
            ...toolNode,
            data: {
              ...toolNode.data,
              errorMessage: `${attr.key}:deleted`,
              attributes: {
                ...(toolNode.data?.attributes || {}),
                [attr.key]: {
                  ...(typeof storedAttr === 'object' ? storedAttr : { value: selectedId }),
                  value: '',
                  id: '',
                  name: ''
                }
              }
            }
          };
          setSelectedNode(updatedNode);
          setDrawerOpen(true);
          setNodes((nds) => nds.map((n) => (n.id === toolNode.id ? updatedNode : n)));
          if (onValidationError) {
            onValidationError(
              `${toolName} references a deleted connected app for ${attrLabel}. Please select a valid configuration.`,
              'error'
            );
          }
          return false;
        }
      }
    }

    return true;
  };
};

// Helper function to validate node data content (LLM models, agent properties)
export const createValidateNodeDataContent = (setSelectedNode, setDrawerOpen, setNodes, toolNodesOrGetter = []) => {
  const getToolNodes = () =>
    typeof toolNodesOrGetter === 'function' ? toolNodesOrGetter() || [] : toolNodesOrGetter || [];

  return (currentNodes, currentEdges, onValidationError) => {
    const toolNodes = getToolNodes();
    // LLM model validation
    const validateModelLlmNode = currentNodes.find(
      (n) => n.type === 'config' && n.data.configType === 'llm' && (!n.data.model || n.data.model === '')
    );
    if (validateModelLlmNode) {
      const updatedNode = { ...validateModelLlmNode, data: { ...validateModelLlmNode.data, errorMessage: 'Model name is required' } };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === validateModelLlmNode.id ? updatedNode : n)));
      if (onValidationError) onValidationError('LLM model name is required. Please configure the LLM node.', 'warning');
      return false;
    }
    
    // Agent name validation
    const validateAgentName = currentNodes.find(
      (n) => (n.type === 'agent' || n.type === 'task') && (
        !n.data.title ||
        n.data.title === '' ||
        n.data.title === "Task Agent" ||
        n.data.title === 'Default Agent' ||
        n.data.title === 'Agent')
    );
    if (validateAgentName) {
      const updatedNode = { ...validateAgentName, data: { ...validateAgentName.data, errorMessage: 'title' } };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === validateAgentName.id ? updatedNode : n)));
      return false;
    }

    // Duplicate agent/task name validation
    const agentTaskNodes = currentNodes.filter((n) => n.type === 'agent' || n.type === 'task');
    const titleCounts = new Map();
    agentTaskNodes.forEach((n) => {
      const title = (n.data?.title || '').trim().toLowerCase();
      if (!title) return;
      if (!titleCounts.has(title)) titleCounts.set(title, []);
      titleCounts.get(title).push(n);
    });
    const duplicateAgentNode = agentTaskNodes.find((n) => {
      const title = (n.data?.title || '').trim().toLowerCase();
      return title && (titleCounts.get(title)?.length || 0) > 1;
    });
    if (duplicateAgentNode) {
      const updatedNode = {
        ...duplicateAgentNode,
        data: { ...duplicateAgentNode.data, errorMessage: 'duplicate_title' }
      };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === duplicateAgentNode.id ? updatedNode : n)));
      if (onValidationError) {
        onValidationError('Agent name must be unique. Please use a different name for each agent.', 'warning');
      }
      return false;
    }
    
    // Agent Role Validation
    const validateAgentRoleNode = currentNodes.find(
      (n) => n.type === 'agent' && (!n.data.role || n.data.role === '')
    );
    if (validateAgentRoleNode) {
      const updatedNode = { ...validateAgentRoleNode, data: { ...validateAgentRoleNode.data, errorMessage: 'role' } };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === validateAgentRoleNode.id ? updatedNode : n)));
      if (onValidationError) onValidationError('Agent role is required. Please configure the agent node.', 'warning');
      return false;
    }
    
    // Agent goal validation
    const validateAgentGoalNode = currentNodes.find(
      (n) => n.type === 'agent' && (!n.data.goal || n.data.goal === '')
    );
    if (validateAgentGoalNode) {
      const updatedNode = { ...validateAgentGoalNode, data: { ...validateAgentGoalNode.data, errorMessage: 'goal' } };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === validateAgentGoalNode.id ? updatedNode : n)));
      if (onValidationError) onValidationError('Agent goal is required. Please configure the agent node.', 'warning');
      return false;
    }
    
    // Tool validations
    const toolConfigNodes = currentNodes.filter(
      n => n.type === 'config' && n.data.configType === 'tool'
    );

    // 1) Tool display name uniqueness validation:
    //    Same display name cannot be used by more than one tool node.
    if (toolConfigNodes.length > 0) {
      const displayNameMap = new Map(); // name -> nodes[]

      toolConfigNodes.forEach(toolNode => {
        const handler = toolNode.data?.handlerName;
        if (!handler) return;

        const toolDefinition = toolNodes.find(t => t.handlerName === handler);
        const effectiveName = (toolNode.data?.displayName || toolDefinition?.displayName || '').trim();
        if (!effectiveName) return;

        const list = displayNameMap.get(effectiveName) || [];
        list.push(toolNode);
        displayNameMap.set(effectiveName, list);
      });

      for (const [name, nodesWithName] of displayNameMap.entries()) {
        if (nodesWithName.length > 1) {
          // Focus the latest conflicting node
          const targetNode = nodesWithName[nodesWithName.length - 1];
          const updatedNode = {
            ...targetNode,
            data: {
              ...targetNode.data,
              errorMessage: 'Display name is already used by another tool. Please choose a unique name.'
            }
          };

          setSelectedNode(updatedNode);
          setDrawerOpen(true);
          setNodes(nds =>
            nds.map(n => (n.id === targetNode.id ? updatedNode : n))
          );

          if (onValidationError) {
            onValidationError(
              `Tool display name "${name}" is already used by another tool. Please choose a unique name.`,
              'error'
            );
          }

          return false;
        }
      }
    }

    // 2) Tool required attributes validation
    const toolsWithMissingAttributes = [];
    
    toolConfigNodes.forEach(toolNode => {
      // Skip MCPHandler tools as they have different structure
      if (toolNode.data.handlerName === 'MCPHandler') return;
      
      // Find the tool definition from toolNodes
      const toolDefinition = toolNodes.find(t => t.handlerName === toolNode.data.handlerName);
      if (!toolDefinition || !toolDefinition.attributes) return;
      
      // Check each required attribute
      const missingAttributes = [];
      toolDefinition.attributes.forEach(attr => {
        if (attr.required) {
          const attrValue = getAttributeValueFromNode(toolNode.data.attributes, attr.key);
          // Check if attribute value is empty or undefined
          if (attrValue === '' || attrValue === null || attrValue === undefined) {
            missingAttributes.push(attr.key);
          }
        }
      });
      
      if (missingAttributes.length > 0) {
        toolsWithMissingAttributes.push({
          node: toolNode,
          toolName: toolDefinition.displayName || toolNode.data.handlerName,
          missingAttributes: missingAttributes
        });
      }
    });
    
    if (toolsWithMissingAttributes.length > 0) {
      const firstTool = toolsWithMissingAttributes[0];
      const missingAttrsStr = firstTool.missingAttributes
        .map(key => key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' '))
        .join(', ');
      const updatedNode = { 
        ...firstTool.node, 
        data: { ...firstTool.node.data, errorMessage: firstTool.missingAttributes[0] } 
      };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === firstTool.node.id ? updatedNode : n)));
      if (onValidationError) {
        onValidationError(
          `${firstTool.toolName} has missing required attributes: ${missingAttrsStr}. Please configure the tool node.`,
          'error'
        );
      }
      return false;
    }
    
    // Task agent instructions validation - check if task has tool but no instructions
    const taskNodes = currentNodes.filter(n => n.type === 'task');
    const taskAgentsWithoutInstructions = taskNodes.filter(task => {
      // Check if task has a tool connected
      const outgoing = currentEdges.filter(e => e.source === task.id && e.sourceHandle === 'tool');
      const toolEdge = outgoing.find(e => {
        const targetNode = currentNodes.find(n => n.id === e.target);
        return targetNode && targetNode.type === 'config' && targetNode.data.configType === 'tool';
      });
      
      // If task has a tool connected, check if it has instructions
      if (toolEdge) {
        const instructionGroups = task.data?.instructionGroups || [];
        const instructions = task.data?.instructions || [];
        // Check if instructionGroups is empty or instructions array is empty
        return instructionGroups.length === 0 && instructions.length === 0;
      }
      return false;
    });
    
    if (taskAgentsWithoutInstructions.length > 0) {
      const taskNode = taskAgentsWithoutInstructions[0];
      const updatedNode = { ...taskNode, data: { ...taskNode.data, errorMessage: 'instructions' } };
      setSelectedNode(updatedNode);
      setDrawerOpen(true);
      setNodes((nds) => nds.map((n) => (n.id === taskNode.id ? updatedNode : n)));
      if (onValidationError) onValidationError('Task agent has a tool connected but no instructions. Please add instructions', 'error');
      return false;
    }
    
    // Clear error messages from nodes that are now valid
    setNodes((nds) =>
      nds.map((n) => {
        if (n.data?.errorMessage) {
          let shouldClear = false;
          
          if (n.type === 'config' && n.data.configType === 'llm' && n.data.model) {
            shouldClear = true;
          } else if (n.type === 'agent' && n.data.title && n.data.role && n.data.goal) {
            shouldClear = true;
          } else if (n.type === 'task' && n.data?.errorMessage === 'instructions' && 
                     ((n.data?.instructionGroups?.length > 0) || (n.data?.instructions?.length > 0))) {
            shouldClear = true;
          } else if (n.type === 'config' && n.data.configType === 'tool') {
            // Check if tool has all required attributes
            if (n.data.handlerName !== 'MCPHandler') {
              const toolDefinition = toolNodes.find(t => t.handlerName === n.data.handlerName);
              if (toolDefinition && toolDefinition.attributes) {
                const allRequiredSet = toolDefinition.attributes
                  .filter(attr => attr.required)
                  .every(attr => {
                    const attrValue = getAttributeValueFromNode(n.data.attributes, attr.key);
                    return attrValue !== '' && attrValue !== null && attrValue !== undefined;
                  });
                if (allRequiredSet) {
                  shouldClear = true;
                }
              }
            }

            // Clear display name conflict error once the name is unique again
            if (
              n.data.errorMessage &&
              n.data.errorMessage.toLowerCase().includes('display name')
            ) {
              const name = n.data.displayName;
              const handler = n.data.handlerName;
              if (name && handler) {
                const othersWithSameName = nds.filter(other =>
                  other.id !== n.id &&
                  other.type === 'config' &&
                  other.data?.configType === 'tool' &&
                  (other.data?.displayName === name ||
                    (!other.data?.displayName &&
                      toolNodes.find(t => t.handlerName === other.data?.handlerName)?.displayName === name))
                );
                if (othersWithSameName.length === 0) {
                  shouldClear = true;
                }
              }
            }
          }
          
          if (shouldClear) {
            return { ...n, data: { ...n.data, errorMessage: undefined } };
          }
        }
        return n;
      })
    );
    
    return true;
  };
};
